import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useTw } from '../../../../../../../utils/Styles'
import { getHighestStatus } from '../../../../../../../utils'
import { StatusPageQuery } from '../../../StatusPage.generated'
import { StatusPageEventHistoryItemComponentStatus as CompStatus } from '../../../../../../../api/types'

type StatusProps = {
  componentId: string
  status: StatusPageQuery['Profile']['statusPage']['components'][0]['eventsHistory'][0]
}

export function Status({ status, componentId }: StatusProps) {
  const tw = useTw()
  const [t] = useTranslation('common')

  const timeline = useMemo(
    () =>
      status.events
        .filter((_) => _.ongoing)
        .flatMap((event) =>
          event.historyItems.filter((_) =>
            _.affectedComponents.some(
              (_: { componentId: string }) => _.componentId === componentId
            )
          )
        ),
    [componentId, status]
  )

  const highestStatus = useMemo(
    () =>
      getHighestStatus(
        timeline.flatMap((timelineEntry) =>
          timelineEntry.affectedComponents
            .filter((component) => component.componentId === componentId)
            .map((_) => _.componentStatus)
        )
      ),
    [componentId, timeline]
  )

  return (
    <div className={tw`flex flex-row items-center space-x-2`}>
      <div className={tw`relative flex h-3 w-3`}>
        <span
          className={tw`
            animate-ping absolute inline-flex h-full w-full rounded-full opacity-75
            ${highestStatus === CompStatus.MajorOutage && 'bg-red-400'}
            ${highestStatus === CompStatus.PartialOutage && 'bg-red-300'}
            ${highestStatus === CompStatus.Degraded && 'bg-yellow-300'}
            ${highestStatus === CompStatus.Operational && 'bg-green-400'}
          `}
        />
        <span
          className={tw`
            relative inline-flex rounded-full h-3 w-3
            ${highestStatus === CompStatus.MajorOutage && 'bg-red-500'}
            ${highestStatus === CompStatus.PartialOutage && 'bg-red-400'}
            ${highestStatus === CompStatus.Degraded && 'bg-yellow-400'}
            ${highestStatus === CompStatus.Operational && 'bg-green-500'}
          `}
        />
      </div>
      <span className={tw`text-neutral-350`}>
        {t(`status_page.component_status.${highestStatus}`)}
      </span>
    </div>
  )
}
